export const ACTIVITY_FEED_LIVE_REQUEST_TIMEOUT_MS = 12000;

export const activityFeedLiveResponseRequiresResync = ({ ok, data } = {}) => (
  (!ok || !data?.success) && data?.resync_required === true
);

const cancellationError = (name, message) => {
  const error = new Error(message);
  error.name = name;
  return error;
};

export const requestActivityFeedLivePage = async ({
  url,
  signal,
  fetchImpl = fetch,
  timeoutMs = ACTIVITY_FEED_LIVE_REQUEST_TIMEOUT_MS,
} = {}) => {
  if (!url || typeof fetchImpl !== 'function') {
    throw new Error('The live activity feed could not be refreshed.');
  }

  const normalizedTimeoutMs = Number(timeoutMs);
  if (!Number.isFinite(normalizedTimeoutMs) || normalizedTimeoutMs <= 0) {
    throw new Error('A valid live activity timeout is required.');
  }
  if (signal?.aborted) {
    throw cancellationError('AbortError', 'The live activity refresh was cancelled.');
  }

  const controller = new AbortController();
  let cancelRequest;
  const cancellation = new Promise((_, reject) => {
    cancelRequest = reject;
  });
  const handleExternalAbort = () => {
    cancelRequest(cancellationError('AbortError', 'The live activity refresh was cancelled.'));
    controller.abort();
  };

  signal?.addEventListener?.('abort', handleExternalAbort, { once: true });
  const timeoutId = setTimeout(() => {
    cancelRequest(cancellationError('TimeoutError', 'The live activity refresh timed out.'));
    controller.abort();
  }, normalizedTimeoutMs);

  try {
    const response = await Promise.race([
      fetchImpl(String(url), {
        credentials: 'include',
        signal: controller.signal,
      }),
      cancellation,
    ]);
    const data = await Promise.race([response.json(), cancellation]);
    return { ok: response.ok, data };
  } finally {
    clearTimeout(timeoutId);
    signal?.removeEventListener?.('abort', handleExternalAbort);
  }
};

export const createActivityFeedLivePollGate = () => {
  let activeRun = null;
  let rerunRequested = false;

  return {
    begin(run) {
      if (activeRun) {
        rerunRequested = true;
        return false;
      }
      activeRun = run;
      return true;
    },
    hasActiveRun() {
      return activeRun !== null;
    },
    requestRerun() {
      rerunRequested = true;
    },
    cancelRerun() {
      rerunRequested = false;
    },
    abortActiveRun() {
      activeRun?.controller?.abort();
    },
    finish(run) {
      if (activeRun !== run) {
        return { released: false, rerunRequested: false };
      }

      activeRun = null;
      const shouldRerun = rerunRequested;
      rerunRequested = false;
      return { released: true, rerunRequested: shouldRerun };
    },
  };
};
